import { type FillableColumn } from '@/components/UI/DataTableV2/DataTable.model.ts';
import DatePickerNative from '@/components/UI/DatePickerNative/DatePickerNative.tsx';
import { DateTimePickerNative } from '@/components/UI/DateTimePickerNative/DateTimePickerNative.tsx';
import { Duration, normalize as normalizeDuration } from '@/components/UI/FormElements/Duration/Duration.tsx';
import Radio from '@/components/UI/FormElements/Radio/Radio.tsx';
import Switch from '@/components/UI/FormElements/Switch';
import { errorToast, isDateGreaterThanToday } from '@/helpers.tsx';
import { _notNil } from '@/littledash.ts';
import cn from 'classnames';
import type { DataTableColumnFillRequestV1, DataTableColumnFillType } from '@/model/DataTable.model.ts';
import { DateUtils } from '@/utils/Date.utils.ts';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal.tsx';
import { parseISO, roundToNearestHours } from 'date-fns';
import { type FC, useMemo } from 'react';
import { Controller, useForm, useWatch, type Validate } from 'react-hook-form';
import InVivoError from '@/model/InVivoError';

interface FormData {
  fillMode: 'planned' | 'manual';
  start: string;
  duration: string;
  overwrite: boolean;
  weighedOn: string;
}

const durationValidator: Validate<string, FormData> = (value) => {
  const result = DateUtils.validateDurationRange(value, {
    allow_zero: true,
    allow_negative: false,
  });

  if (!result.valid) {
    switch (result.reason) {
      case 'error_format':
        return 'Invalid duration';
      case 'error_range_min':
        return 'Duration cannot be negative';
      case 'error_range_max':
        return 'Duration exceeds maximum allowed';
    }
  }

  return true;
};

/**
 * Bounds the weighing date at today. `max` on the input is advisory only — `ModalActions` sits
 * outside the form, so native constraint validation never runs — and this fill clears rows with no
 * weight on the chosen date, so a future date would empty the column.
 */
const weighedOnValidator: Validate<string, FormData> = (value) => {
  if (!DateUtils.isValidDate(value)) {
    return 'Enter a valid date';
  }

  return isDateGreaterThanToday(parseISO(value)) ? 'Weighing date cannot be in the future' : true;
};

interface DataTableColumnFillProps {
  column: FillableColumn;
  showPlannedTimeOption?: boolean;
  onFill: (data: DataTableColumnFillRequestV1) => Promise<void>;
  onCancel: () => void;
}

export const DataTableColumnFill: FC<DataTableColumnFillProps> = ({
  column,
  showPlannedTimeOption = false,
  onFill,
  onCancel,
}) => {
  const formMethods = useForm<FormData>({
    mode: 'onChange',
    reValidateMode: 'onBlur',
    defaultValues: {
      fillMode: 'manual',
      start: roundToNearestHours(new Date()).toISOString(),
      duration:
        column.type === 'timestampBaseline' && _notNil(column.pk?.interval)
          ? normalizeDuration({ minutes: Math.floor(column.pk.interval / 60) })
          : 'PT30M',
      overwrite: false,
      weighedOn: DateUtils.dateNow(),
    },
  });
  const fillData = useMemo<{ type: DataTableColumnFillType; label: string }>(() => {
    switch (column.type) {
      case 'sampleCollectedAt':
        return { type: 'sample_collected_at', label: 'Time between collections' };
      case 'sampleDoseTime':
        return { type: 'sample_dose_time', label: 'Time between doses' };
      case 'timestampBaseline':
        return { type: 'pk_dose', label: 'Time between doses' };
      case 'dosedAt':
        return { type: 'dosed_at', label: 'Time between doses' };
      case 'doseWeight':
        return { type: 'dose_weight', label: 'Weighing date' };
    }
    // @ts-expect-error: exhaustive check
    throw new InVivoError('Unsupported column type', { level: 'fatal', context: { type: column.type } });
  }, [column]);
  const fillMode = useWatch({ control: formMethods.control, name: 'fillMode' });

  const isDateFill = fillData.type === 'dose_weight';
  const showManualFields = !isDateFill && (!showPlannedTimeOption || fillMode === 'manual');
  const handleSubmit = async (formData: FormData) => {
    try {
      switch (fillData.type) {
        case 'dose_weight':
          await onFill({ type: fillData.type, measured_on: formData.weighedOn });
          break;
        case 'sample_collected_at':
          await onFill(
            showManualFields
              ? {
                  type: fillData.type,
                  start: formData.start,
                  duration: formData.duration,
                  overwrite: formData.overwrite,
                }
              : { type: fillData.type, overwrite: formData.overwrite }
          );
          break;
        case 'pk_dose':
        case 'sample_dose_time':
        case 'dosed_at':
          await onFill({
            type: fillData.type,
            start: formData.start,
            duration: formData.duration,
            overwrite: formData.overwrite,
          });
          break;
      }
    } catch {
      errorToast('Could not fill column');
    }
  };
  return (
    <ModalContainer size="narrow">
      <ModalHeader title="Fill column" />
      <form
        onSubmit={formMethods.handleSubmit(handleSubmit)}
        data-test-component="DataTableColumnFill"
        data-test-element="form"
      >
        {showPlannedTimeOption && !isDateFill && (
          <Controller
            control={formMethods.control}
            name="fillMode"
            render={({ field }) => (
              <div className="pa3" data-test-element="fill-mode-field">
                <Radio
                  id="fill-mode-planned"
                  className="pb2"
                  value="planned"
                  name={field.name}
                  label="Use planned time"
                  checked={field.value === 'planned'}
                  onChange={() => field.onChange('planned')}
                />
                <Radio
                  id="fill-mode-manual"
                  value="manual"
                  name={field.name}
                  label="Define start and intervals"
                  checked={field.value === 'manual'}
                  onChange={() => field.onChange('manual')}
                />
              </div>
            )}
          />
        )}
        {isDateFill && (
          <Controller
            control={formMethods.control}
            name="weighedOn"
            shouldUnregister
            rules={{ validate: weighedOnValidator }}
            render={({ field, fieldState }) => (
              <div className="pa3" data-test-element="weighed-on-field">
                <div className="flex justify-between flex-wrap items-center align-center">
                  <label htmlFor="fill-weighed-on">{fillData.label}</label>
                  <DatePickerNative
                    id="fill-weighed-on"
                    style={{ marginBottom: 0 }}
                    value={field.value}
                    max={DateUtils.dateNow()}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                </div>
                <small className="db mt2">Rows with no weight recorded on this date will be cleared.</small>
                {_notNil(fieldState.error?.message) && (
                  <p className="red f6 mt2 mb0" data-test-element="weighed-on-error">
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />
        )}
        {showManualFields && (
          <>
            <Controller
              control={formMethods.control}
              name="start"
              shouldUnregister
              render={({ field }) => (
                <div
                  className={cn('pa3 flex justify-between flex-wrap items-center align-center', {
                    'bt b--moon-gray': showPlannedTimeOption,
                  })}
                  data-test-element="start-field"
                >
                  <label htmlFor="fill-start">Start time</label>
                  <DateTimePickerNative
                    id="fill-start"
                    style={{ marginBottom: 0 }}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                </div>
              )}
            />
            <Controller
              control={formMethods.control}
              name="duration"
              shouldUnregister
              rules={{ validate: durationValidator }}
              render={({ field, fieldState }) => (
                <div
                  className="pa3 bt b--moon-gray flex justify-between flex-wrap items-center align-center"
                  data-test-element="duration-field"
                  role="group"
                  aria-labelledby="fill-duration-label"
                >
                  {/* `Duration` renders several inputs, so the group is named rather than one of them. */}
                  <label id="fill-duration-label">{fillData.label}</label>
                  <Duration
                    value={field.value}
                    disabled={field.disabled}
                    error={fieldState.invalid}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                </div>
              )}
            />
          </>
        )}
        {!isDateFill && (
          <Controller
            control={formMethods.control}
            name="overwrite"
            render={({ field }) => (
              <div
                className="pa3 bt b--moon-gray flex justify-between flex-wrap items-center align-center"
                data-test-element="overwrite-field"
              >
                <span>
                  <label>Overwrite existing values</label>
                  <small>All timestamps in this column will be replaced</small>
                </span>
                <Switch
                  ariaLabel="Overwrite existing values"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              </div>
            )}
          />
        )}
      </form>
      <ModalActions
        className="pa3 bt b--moon-gray"
        onSubmit={formMethods.handleSubmit(handleSubmit)}
        submitBtnText="Fill"
        submitButtonProps={{
          disabled: !formMethods.formState.isValid && formMethods.formState.isDirty,
        }}
        onCancel={onCancel}
        cancelBtnText="Cancel"
      />
    </ModalContainer>
  );
};
